import { useCallback, useEffect, useReducer, useRef } from 'react'

import {
  applyAction,
  createRng,
  riddlesEngine,
  toRiddleView,
  type RiddleAction,
  type RiddleState,
  type RiddleView,
  type SkillLevels,
} from '@hazel/shared'

import { api } from '../api/client.js'
import type { DealtRound } from './useRound.js'
import { useMoveLog } from './useMoveLog.js'
import { useMilestone } from './useMilestone.js'

const SOLO = 'solo'
const PLAYER = { id: SOLO, displayName: 'You', userId: null } as const

interface Round {
  readonly seed: string
  readonly state: RiddleState
  readonly actions: readonly RiddleAction[]
}

type RoundEvent = { type: 'answer'; action: RiddleAction; now: number }

function start(seed: string, levels: SkillLevels, now: number): Round {
  return {
    seed,
    state: riddlesEngine.createInitialState({ levels, startedAt: now }, [PLAYER], createRng(seed)),
    actions: [],
  }
}

/** Pure, so React re-running it cannot record a round twice. */
function reduceRound(round: Round, event: RoundEvent): Round {
  const outcome = applyAction(riddlesEngine, round.state, event.action, {
    playerId: SOLO,
    now: event.now,
  })

  if (!outcome.ok) return round

  return { ...round, state: outcome.state, actions: [...round.actions, event.action] }
}

export interface Riddles {
  readonly view: RiddleView
  readonly answer: (value: number) => void
  readonly again: () => void
  readonly finished: boolean
  /** The milestone this round crossed, if it crossed one. Null the rest of the time. */
  readonly milestone: number | null
}

/**
 * Rocky's Riddles, running the shared engine in the browser.
 *
 * The same shape as Monkey's Maths and Turtley's Trolley — the round carries
 * the seed and the levels it was dealt from, so it can only be checked
 * afterwards against the levels it was actually dealt from.
 *
 * Its levels are its own, loaded and written under the riddles game key.
 * Reading a row of clues and knowing your times tables are unrelated, and one
 * must never move the other.
 */
export function useRiddles(
  round: DealtRound<{ levels: SkillLevels }>,
  onFinished: () => void,
): Riddles {
  const [state, dispatch] = useReducer(reduceRound, round.seed, (seed) =>
    start(seed, round.config.levels, Date.now()),
  )

  const moves = useMoveLog(round.roundId)

  const award = useMilestone()
  const recorded = useRef(false)
  const finished = state.state.status !== 'playing'

  useEffect(() => {
    if (!finished || recorded.current) return
    recorded.current = true

    // Every answer, with the moment it was given, is already at the server.
    // The timing is not incidental: fluency is judged by the clock, and a round
    // reconstructed without it scores every answer as instant.
    void moves
      .flush()
      .then((told) => {
        // Nothing to score if the server never heard the whole round. The round
        // is not spent, so it can still be finished from another device — and
        // asking now would only have it refused.
        if (!told) return undefined

        return api('/api/plays/riddles', {
          method: 'POST',
          body: JSON.stringify({ roundId: round.roundId }),
        })
      })
      .then(award.notice)
      // A failure to record must not interrupt anything. She answered the
      // questions either way.
      .catch(() => undefined)
  }, [finished, round.roundId, moves, award.notice])

  return {
    view: toRiddleView(state.state),
    answer: useCallback(
      (value: number) => {
        const now = Date.now()
        const action = { type: 'answer', value } as const

        dispatch({ type: 'answer', action, now })
        // Never earlier than the answer before it: a device whose clock is
        // corrected mid-round would otherwise send times that run backwards.
        moves.record(action, Math.max(0, now - state.state.startedAt))
      },
      [moves, state.state.startedAt],
    ),
    again: onFinished,
    finished,
    milestone: award.milestone,
  }
}
